import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge, Box, Button, Card, CardBody, CardHeader, Flex, FormControl, Heading,
  IconButton, Input, Select, Text, Tooltip, VStack, useToast,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import settingsService from '../services/settingsService';

const MAX_LENGTH = 60; // hotels.label_overrides.LABEL_OVERRIDE_MAX_LENGTH

const GROUP_COLORS = { reservation: 'blue', guest: 'purple', create: 'green' };

/**
 * Hotel Settings → Labels.
 *
 * Lets a property rename staff-PMS labels on the reservation detail
 * page, guest profile and Quick Reservation panel. Deliberately NOT a
 * wall of every label: the card lists only the labels this property has
 * renamed, and "Add a label" picks one more at a time from the catalog
 * the backend serves (settings.labelOverrideCatalog — the single source
 * of truth, see backend hotels/label_overrides.py). The whole set is
 * saved in one PUT; a removed row simply drops out of the dict.
 */
const LabelOverridesCard = ({ settings, onSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();

  const catalog = useMemo(() => settings?.labelOverrideCatalog || [], [settings]);
  const groups = useMemo(() => settings?.labelOverrideGroups || [], [settings]);
  const byKey = useMemo(
    () => Object.fromEntries(catalog.map((row) => [row.key, row])),
    [catalog]
  );

  // Rows in the order they were added; [{ key, text }]
  const [rows, setRows] = useState([]);
  const [pickKey, setPickKey] = useState('');
  const [pickText, setPickText] = useState('');
  const [saving, setSaving] = useState(false);

  const saved = useMemo(() => settings?.labelOverrides || {}, [settings]);
  useEffect(() => {
    setRows(Object.entries(saved).map(([key, text]) => ({ key, text })));
  }, [saved]);

  const asDict = (list) => Object.fromEntries(
    list.filter((r) => r.text.trim()).map((r) => [r.key, r.text.trim()])
  );
  const dirty = JSON.stringify(asDict(rows)) !== JSON.stringify(saved);
  const tooLong = rows.some((r) => r.text.trim().length > MAX_LENGTH);

  const usedKeys = new Set(rows.map((r) => r.key));
  const available = catalog.filter((row) => !usedKeys.has(row.key));

  const addRow = () => {
    if (!pickKey || !pickText.trim()) return;
    setRows((prev) => [...prev, { key: pickKey, text: pickText }]);
    setPickKey('');
    setPickText('');
  };

  const save = async () => {
    setSaving(true);
    const next = asDict(rows);
    try {
      await settingsService.updateSettings({ labelOverrides: next });
      onSaved(next);
      toast({ title: t('hotelSettings.labels.saved'), status: 'success', duration: 2000 });
    } catch (err) {
      const detail = err.response?.data?.details?.labelOverrides || err.response?.data?.detail;
      toast({ title: t('hotelSettings.toasts.saveFailed'), description: detail, status: 'error', duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const groupLabel = (key) => {
    const g = groups.find((x) => x.key === key);
    return t(`hotelSettings.labels.groups.${key}`, { defaultValue: g?.label || key });
  };

  if (!catalog.length) return null; // backend without the feature yet

  return (
    <Card shadow="sm" borderWidth="1px">
      <CardHeader pb={2}>
        <Flex align="center" gap={3} wrap="wrap">
          <Box flex="1" minW="200px">
            <Heading size="sm">{t('hotelSettings.labels.title')}</Heading>
            <Text fontSize="xs" color="gray.400" mt={1}>
              {t('hotelSettings.labels.help')}
            </Text>
          </Box>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={save}
            isLoading={saving}
            isDisabled={!dirty || tooLong}
          >
            {t('hotelSettings.labels.save')}
          </Button>
        </Flex>
      </CardHeader>
      <CardBody pt={2}>
        <VStack align="stretch" spacing={2}>
          {rows.length === 0 && (
            <Text fontSize="sm" color="gray.500">{t('hotelSettings.labels.empty')}</Text>
          )}

          {rows.map((row, i) => {
            const meta = byKey[row.key];
            const over = row.text.trim().length > MAX_LENGTH;
            return (
              <Flex key={row.key} align="center" gap={3} wrap={{ base: 'wrap', md: 'nowrap' }}
                p={2} borderWidth="1px" borderRadius="md" borderColor="gray.100">
                <Box minW={{ base: '100%', md: '260px' }}>
                  <Badge colorScheme={GROUP_COLORS[meta?.group] || 'gray'} fontSize="2xs" mb={1}>
                    {groupLabel(meta?.group)}
                  </Badge>
                  <Text fontSize="sm" color="gray.600">
                    {meta?.default || row.key}
                  </Text>
                  {meta?.uses > 1 && (
                    <Text fontSize="xs" color="gray.400">
                      {t('hotelSettings.labels.uses', { count: meta.uses })}
                    </Text>
                  )}
                </Box>
                <FormControl isInvalid={over || !row.text.trim()} flex="1">
                  <Input
                    size="sm"
                    value={row.text}
                    maxLength={MAX_LENGTH + 20}
                    aria-label={t('hotelSettings.labels.newLabel')}
                    onChange={(e) => {
                      const text = e.target.value;
                      setRows((prev) => prev.map((r, j) => (j === i ? { ...r, text } : r)));
                    }}
                  />
                </FormControl>
                <Tooltip label={t('hotelSettings.labels.remove')}>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    icon={<DeleteIcon />}
                    aria-label={t('hotelSettings.labels.remove')}
                    onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
                  />
                </Tooltip>
              </Flex>
            );
          })}

          {/* Add one label at a time — only labels not already renamed */}
          {available.length > 0 && (
            <Flex gap={3} wrap={{ base: 'wrap', md: 'nowrap' }} pt={2} align="center">
              <Select
                size="sm"
                minW={{ base: '100%', md: '260px' }}
                maxW={{ md: '260px' }}
                placeholder={t('hotelSettings.labels.pickPlaceholder')}
                value={pickKey}
                onChange={(e) => setPickKey(e.target.value)}
              >
                {groups.map((g) => {
                  const items = available.filter((row) => row.group === g.key);
                  if (!items.length) return null;
                  return (
                    <optgroup key={g.key} label={groupLabel(g.key)}>
                      {items.map((row) => (
                        <option key={row.key} value={row.key}>{row.default}</option>
                      ))}
                    </optgroup>
                  );
                })}
              </Select>
              <Input
                size="sm"
                flex="1"
                placeholder={t('hotelSettings.labels.newLabelPlaceholder')}
                value={pickText}
                maxLength={MAX_LENGTH}
                isDisabled={!pickKey}
                onChange={(e) => setPickText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRow(); } }}
              />
              <Button
                size="sm"
                leftIcon={<AddIcon />}
                onClick={addRow}
                isDisabled={!pickKey || !pickText.trim()}
              >
                {t('hotelSettings.labels.add')}
              </Button>
            </Flex>
          )}
          {dirty && (
            <Text fontSize="xs" color="orange.500">{t('hotelSettings.labels.unsaved')}</Text>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default LabelOverridesCard;
